import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { getArticleBySlug } from '@/mocks/articles';

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : null;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">
              文章未找到
            </h1>
            <p className="text-foreground-500 text-sm mb-4">
              请检查文章链接是否正确
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `category:${article.category}`, href: `/category/${article.category}` },
    { label: article.title },
  ];

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          title: article.title,
          summary: article.summary,
          image: article.image,
          date: article.date,
          slug: article.slug,
          tags: article.tags,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          items: [
            { name: '首页', url: 'https://ntrinsight.github.io/' },
            { name: getCategoryName(article.category), url: `https://ntrinsight.github.io/category/${article.category}` },
            { name: article.title, url: `https://ntrinsight.github.io/article/${article.slug}` },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-100 transition-colors"
                >
                  {getCategoryName(article.category)}
                </Link>
                <span className="text-xs text-foreground-500">
                  <i className="ri-calendar-line mr-1"></i>
                  {article.date}
                </span>
                <span className="text-xs text-foreground-500">
                  <i className="ri-time-line mr-1"></i>
                  {article.readTime} 分钟阅读
                </span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                {article.summary}
              </p>
            </header>

            {/* Featured Image */}
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            {/* Content */}
            <div
              className="article-content mb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8 pt-6 border-t border-background-200">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-foreground-600 bg-background-100 px-3 py-1 rounded whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-background-200">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回 {getCategoryName(article.category)}
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}